import React, { useMemo } from 'react'
import {
  Alert,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import StarIcon from '@mui/icons-material/Star'
import { Trans } from '@lingui/macro'
import { formatPersonDisplayName } from '../match-display-rows'
import { buildMatchPairBindings, getMatchPercentDisplay } from '../match-pairs'

/**
 * @param {object} props
 * @param {Record<string, unknown>[]} props.anchorPeople
 * @param {Record<string, unknown> | null | undefined} props.matchDetail
 * @param {string} props.matchRole
 * @param {string} props.matchRoleLabel
 * @param {string} props.anchorRoleLabel
 * @param {Record<string, string>} props.collisionChoices
 * @param {(matchPersonKey: string, anchorPersonKey: string) => void} props.onCollisionChoice
 */
export function MatchPairingsPanel({
  anchorPeople,
  matchDetail,
  matchRole,
  matchRoleLabel,
  anchorRoleLabel,
  collisionChoices = {},
  onCollisionChoice,
}) {
  const bindings = useMemo(
    () => buildMatchPairBindings(anchorPeople, matchDetail, matchRole, collisionChoices),
    [anchorPeople, matchDetail, matchRole, collisionChoices],
  )

  const hasApiCandidates = useMemo(() => {
    const metas = Array.isArray(matchDetail?._matchMetas) && matchDetail._matchMetas.length > 0
      ? matchDetail._matchMetas
      : (matchDetail?.candidate_name || matchDetail?.candidate_surname)
        ? [matchDetail]
        : []
    return metas.some((meta) => meta?.candidate_name || meta?.candidate_surname)
  }, [matchDetail])

  if (!matchDetail) {
    return null
  }

  if (!anchorPeople?.length) {
    return (
      <Alert severity="info" sx={{ mt: 1.5 }}>
        <Trans>Loading people from the selected source crash…</Trans>
      </Alert>
    )
  }

  if (bindings.proposals.length === 0) {
    if (!hasApiCandidates) return null
    return (
      <Alert severity="warning" sx={{ mt: 1.5 }}>
        <Trans>
          Match candidates were found but no person pairing could be built. Check that names on the source crash match the API candidates.
        </Trans>
      </Alert>
    )
  }

  const { confirmed, unresolvedCollisions } = bindings

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        <Trans>Person pairings</Trans>
      </Typography>

      {unresolvedCollisions.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Trans>Multiple people match the same person. Choose who to merge.</Trans>
        </Alert>
      ) : null}

      <Stack gap={1}>
        {confirmed.map((pair) => (
          <Box
            key={`${pair.anchorPersonKey}-${pair.matchPersonKey}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              px: 1,
              py: 0.75,
              borderRadius: 1,
              bgcolor: 'success.light',
              color: 'success.contrastText',
            }}
          >
            <Chip
              size="small"
              label={anchorRoleLabel}
              sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontWeight: 700 }}
            />
            <Typography variant="body2" fontWeight={700}>
              {formatPersonDisplayName(pair.anchorPerson)}
            </Typography>
            <ArrowForwardIcon fontSize="small" />
            <Chip
              size="small"
              label={matchRoleLabel}
              sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontWeight: 700 }}
            />
            <Typography variant="body2" fontWeight={700}>
              {formatPersonDisplayName(pair.matchPerson)}
            </Typography>
            {(() => {
              const { displayPercent, showStar } = getMatchPercentDisplay(pair.matchPercent)
              return (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {showStar ? <StarIcon fontSize="small" sx={{ color: 'inherit' }} /> : null}
                  <Chip
                    size="small"
                    label={`${displayPercent.toFixed(0)}%`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'inherit' }}
                  />
                </Box>
              )
            })()}
          </Box>
        ))}

        {unresolvedCollisions.map((collision) => (
          <Box
            key={collision.matchPersonKey}
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.main',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              <Trans>Who merges with</Trans>{' '}
              <strong>{formatPersonDisplayName(collision.matchPerson)}</strong>
              {' '}({matchRoleLabel})?
            </Typography>
            <FormControl component="fieldset" size="small">
              <RadioGroup
                value={collisionChoices[collision.matchPersonKey] ?? ''}
                onChange={(event) => {
                  onCollisionChoice?.(collision.matchPersonKey, event.target.value)
                }}
              >
                {collision.anchorCandidates.map((candidate) => (
                  <FormControlLabel
                    key={candidate.anchorPersonKey}
                    value={candidate.anchorPersonKey}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatPersonDisplayName(candidate.anchorPerson)}
                        </Typography>
                        {(() => {
                          const { displayPercent, showStar } = getMatchPercentDisplay(candidate.matchPercent)
                          return (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                              {showStar ? (
                                <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                              ) : null}
                              <Typography variant="caption" color="text.secondary">
                                ({displayPercent.toFixed(0)}%)
                              </Typography>
                            </Box>
                          )
                        })()}
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        ))}

        {confirmed.length === 0 && unresolvedCollisions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            <Trans>No person pairings found for this match.</Trans>
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  )
}

export default MatchPairingsPanel
